import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { FluentIcon } from '@fluentui/react-icons'
import {
    ArrowSwapRegular,
    ChatBubblesQuestionRegular,
    MicRegular,
    PollRegular,
} from '@fluentui/react-icons'
import { useConfig } from '../../contexts/ConfigContext'
import { useLayoutContext } from '../../contexts/LayoutContext'

export type AdminSectionId = 'questions' | 'polls' | 'exchange' | 'captures'

export interface AdminSection {
    id: AdminSectionId
    label: string
    path: string
    description: string
    Icon: FluentIcon
}

interface SectionFlags {
    engage?: boolean
    communityHub?: boolean
    captures?: boolean
}

const SECTIONS: (AdminSection & {
    enabled: (flags: SectionFlags) => boolean
})[] = [
    {
        id: 'questions',
        label: 'Questions',
        path: '/admin/questions',
        description:
            'Review and approve questions, including anything members have flagged.',
        Icon: ChatBubblesQuestionRegular,
        enabled: (f) => !!f.engage,
    },
    {
        id: 'polls',
        label: 'Polls',
        path: '/admin/polls',
        description: 'Create polls and see results.',
        Icon: PollRegular,
        enabled: (f) => !!f.engage,
    },
    {
        id: 'exchange',
        label: 'Exchange',
        path: '/admin/exchange',
        description: 'Moderate posts in the Knowledge Exchange.',
        Icon: ArrowSwapRegular,
        enabled: (f) => !!f.communityHub,
    },
    {
        id: 'captures',
        label: 'Captures',
        path: '/admin/captures',
        description:
            'Set up recordings for tables and sessions, and follow them live.',
        Icon: MicRegular,
        enabled: (f) => !!f.captures,
    },
]

/**
 * The admin sections this tenant has switched on. A section follows the flag
 * of the page it manages, so admins never see a section users cannot reach.
 */
export function enabledAdminSections(flags: SectionFlags): AdminSection[] {
    return SECTIONS.filter((s) => s.enabled(flags)).map(
        ({ id, label, path, description, Icon }) => ({
            id,
            label,
            path,
            description,
            Icon,
        })
    )
}

export function useAdminSections(): AdminSection[] {
    return enabledAdminSections(useConfig())
}

/**
 * Points the top bar's back arrow one level up, or hides it when `to` is null.
 * The arrow is cleared on unmount so it never leaks onto the next page.
 */
export function useAdminBack(to: string | null) {
    const navigate = useNavigate()
    const { setOnBack } = useLayoutContext()

    useEffect(() => {
        // The setter takes a function, so wrap it: a bare function would be
        // read as a state updater.
        setOnBack(to ? () => () => navigate(to) : null)
        return () => setOnBack(null)
    }, [to, navigate, setOnBack])
}
